import { Head } from "@inertiajs/react";
import { Check, Clipboard, ImageIcon, ImagePlus, Loader2, Palette, RefreshCcw, Sparkles, Upload } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { FinalCta } from "@/components/final-cta";
import PublicHeader from "@/components/public-header";
import { SiteFooter } from "@/components/site-footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Kbd } from "@/components/ui/kbd";
import type { ContrastPair } from "@/lib/color-palette";
import { condensePalette, determineImageContrastColorsPair } from "@/lib/color-palette";
import { cn } from "@/lib/utils";

interface Dimensions {
  width: number;
  height: number;
}

interface PaletteSwatch extends ContrastPair {
  hex: string;
}

const HOW_IT_WORKS_STEPS = [
  {
    icon: Upload,
    title: "Upload image",
    description: "Drop an image anywhere on the page, paste from your clipboard, or click to browse.",
  },
  {
    icon: Sparkles,
    title: "Extract colors",
    description: "The algorithm reads the image right in your browser and condenses it into five dominant colors.",
  },
  {
    icon: Palette,
    title: "Pick and check contrast",
    description:
      "Every color comes with its hex code and a WCAG contrast ratio. Need a specific spot? Click any pixel in your image for its exact hex code.",
  },
  {
    icon: Clipboard,
    title: "Copy codes",
    description: "Copy a single hex code with one click, or grab the whole palette at once for your designs and code.",
  },
];

export default function ImageColorPaletteExtractorPage() {
  const [dragDepth, setDragDepth] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string>("");
  const [dimensions, setDimensions] = useState<Dimensions | null>(null);
  const [palette, setPalette] = useState<string[]>([]);
  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const [copiedAll, setCopiedAll] = useState(false);
  const [copiedHex, setCopiedHex] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pickedColors, setPickedColors] = useState<string[]>([]);
  const [hoverSample, setHoverSample] = useState<{ hex: string; x: number; y: number } | null>(null);
  const objectUrlRef = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const resultsRef = useRef<HTMLElement | null>(null);
  const handleFilesRef = useRef<(f: File) => void>(() => {
    // Replaced with the real handler after render
  });
  const samplerRef = useRef<{ ctx: CanvasRenderingContext2D; width: number; height: number; url: string } | null>(null);

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
      }
    };
  }, []);

  useEffect(() => {
    function onPaste(e: ClipboardEvent) {
      const file = Array.from(e.clipboardData?.files ?? []).find((f) => f.type.startsWith("image/"));
      if (file) {
        e.preventDefault();
        handleFilesRef.current(file);
      }
    }
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, []);

  const palettePairs = useMemo<PaletteSwatch[]>(() => {
    if (palette.length === 0) return [];
    return palette.map((hex) => ({
      hex,
      ...determineImageContrastColorsPair({
        colorPalette: palette,
        backgroundColor: hex,
      }),
    }));
  }, [palette]);

  const selectedPair = useMemo(() => {
    if (!selectedColor || palette.length === 0) return null;
    return determineImageContrastColorsPair({
      colorPalette: palette,
      backgroundColor: selectedColor,
    });
  }, [palette, selectedColor]);

  const handleFiles = useCallback((file: File) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("That file is not an image. Drop a PNG, JPG, WebP, or AVIF instead.");
      return;
    }

    setCopiedAll(false);
    setCopiedHex(null);
    setError(null);
    setPickedColors([]);
    setHoverSample(null);
    samplerRef.current = null;
    setIsProcessing(true);
    setFileName(file.name || "pasted-image");

    const url = URL.createObjectURL(file);
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
    }
    objectUrlRef.current = url;
    setPreviewUrl(url);

    const image = new Image();
    image.src = url;
    image.crossOrigin = "anonymous";

    image.onload = () => {
      try {
        setDimensions({ width: image.width, height: image.height });
        const samples = extractPaletteSamples(image);
        const condensed = condensePalette(samples, { limit: 5 });
        setPalette(condensed);
        setSelectedColor(condensed[0] ?? null);
        window.setTimeout(() => {
          resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 100);
      } catch (ex) {
        console.error(ex);
        setError("We couldn't read colors from that image. Try another file?");
        setPalette([]);
        setSelectedColor(null);
      } finally {
        setIsProcessing(false);
      }
    };

    image.onerror = () => {
      setIsProcessing(false);
      setError("Something went wrong while reading the image. Try another file.");
    };
  }, []);
  handleFilesRef.current = handleFiles;

  const onPageDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setDragDepth(0);
      const file = event.dataTransfer.files?.[0];
      if (file) {
        handleFiles(file);
      }
    },
    [handleFiles],
  );

  const onFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        handleFiles(file);
      }
      // Allow re-selecting the same file.
      event.target.value = "";
    },
    [handleFiles],
  );

  const triggerFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const copyPalette = useCallback(async () => {
    if (palette.length === 0) return;
    try {
      const text = palette.join("\n");
      await navigator.clipboard.writeText(text);
      setCopiedAll(true);
      window.setTimeout(() => setCopiedAll(false), 2000);
    } catch (ex) {
      console.error(ex);
      setError("Clipboard is unavailable in this browser. Copy manually instead.");
    }
  }, [palette]);

  const copyHex = useCallback(async (hex: string) => {
    try {
      await navigator.clipboard.writeText(hex);
      setCopiedHex(hex);
      window.setTimeout(() => setCopiedHex((current) => (current === hex ? null : current)), 2000);
    } catch (ex) {
      console.error(ex);
      setError("Clipboard is unavailable in this browser. Copy manually instead.");
    }
  }, []);

  const samplePixel = useCallback((event: React.PointerEvent<HTMLImageElement>): string | null => {
    const img = event.currentTarget;

    let sampler = samplerRef.current;
    if (!sampler || sampler.url !== img.src) {
      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      if (!ctx) return null;
      ctx.drawImage(img, 0, 0);
      sampler = { ctx, width: canvas.width, height: canvas.height, url: img.src };
      samplerRef.current = sampler;
    }

    const rect = img.getBoundingClientRect();
    const x = Math.floor(((event.clientX - rect.left) / rect.width) * sampler.width);
    const y = Math.floor(((event.clientY - rect.top) / rect.height) * sampler.height);
    if (x < 0 || y < 0 || x >= sampler.width || y >= sampler.height) return null;

    const [r, g, b, a] = sampler.ctx.getImageData(x, y, 1, 1).data;
    if (a < 16) return null;
    return rgbToHex(r, g, b);
  }, []);

  const onPickerMove = useCallback(
    (event: React.PointerEvent<HTMLImageElement>) => {
      const hex = samplePixel(event);
      if (!hex) {
        setHoverSample(null);
        return;
      }
      const rect = event.currentTarget.getBoundingClientRect();
      setHoverSample({ hex, x: event.clientX - rect.left, y: event.clientY - rect.top });
    },
    [samplePixel],
  );

  const onPickerClick = useCallback(
    (event: React.PointerEvent<HTMLImageElement>) => {
      const hex = samplePixel(event);
      if (!hex) return;
      setPickedColors((prev) => [hex, ...prev.filter((c) => c !== hex)].slice(0, 8));
      setSelectedColor(hex);
      void copyHex(hex);
    },
    [samplePixel, copyHex],
  );

  const dragActive = dragDepth > 0;

  return (
    <>
      <Head title="Color Palette from Image - Free Hex Color Picker & Extractor" />
      <div
        className="relative min-h-screen bg-gradient-to-br from-orange-500/10 via-transparent to-red-500/10 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900"
        onDragEnter={(e) => {
          if (e.dataTransfer.types.includes("Files")) setDragDepth((d) => d + 1);
        }}
        onDragLeave={() => setDragDepth((d) => Math.max(0, d - 1))}
        onDragOver={(e) => e.preventDefault()}
        onDrop={onPageDrop}
      >
        {dragActive && (
          <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center bg-orange-500/10 backdrop-blur-[2px]">
            <div className="rounded-2xl border-2 border-dashed border-orange-500 bg-white/95 px-10 py-8 text-center shadow-xl dark:bg-gray-900/95">
              <ImagePlus className="mx-auto h-10 w-10 text-orange-500" />
              <p className="mt-3 text-lg font-semibold text-gray-900 dark:text-gray-100">Drop to extract colors</p>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">PNG, JPG, WebP, or AVIF</p>
            </div>
          </div>
        )}

        <PublicHeader />

        <main className="flex-1">
          <section>
            <div className="mx-auto flex w-full max-w-5xl flex-col gap-10 px-4 py-16 text-center">
              <div className="space-y-6">
                <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-orange-500/20 text-orange-600">
                  <Sparkles className="size-6" />
                </div>
                <div className="space-y-4">
                  <h1 className="text-4xl font-semibold tracking-tight text-balance sm:text-5xl">
                    Get the Color Palette from Any Image
                  </h1>
                  <p className="text-muted-foreground mx-auto max-w-2xl text-lg">
                    A free color picker that extracts the dominant colors from any photo or logo and gives you hex codes
                    with WCAG contrast ratios. Everything runs in your browser, so your image never leaves your device.
                  </p>
                </div>
                <div className="flex flex-wrap items-center justify-center gap-3">
                  <Button size="lg" onClick={triggerFilePicker}>
                    Extract colors from image
                  </Button>
                  <p className="text-muted-foreground text-sm">100% free • No signup required • Works offline</p>
                </div>
              </div>

              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={onFileChange} />

              {previewUrl ? (
                <div className="mx-auto w-full max-w-3xl rounded-2xl border bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
                  <div className="flex items-center gap-4">
                    <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-lg border dark:border-gray-600">
                      <img src={previewUrl} alt="Uploaded preview" className="h-full w-full object-cover" />
                      {isProcessing && (
                        <div className="bg-background/70 absolute inset-0 flex items-center justify-center backdrop-blur">
                          <Loader2 className="size-5 animate-spin" />
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 flex-1 text-left">
                      <div className="truncate text-sm font-medium">{fileName}</div>
                      <div className="text-muted-foreground mt-0.5 flex flex-wrap items-center gap-x-2 text-xs">
                        {dimensions && (
                          <span>
                            {dimensions.width} × {dimensions.height}px
                          </span>
                        )}
                        {palette.length > 0 && <span>· {palette.length} colors found</span>}
                        {isProcessing && <span>· Extracting…</span>}
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={triggerFilePicker}
                      disabled={isProcessing}
                      className="shrink-0"
                    >
                      <RefreshCcw className="size-4" />
                      Replace
                    </Button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={triggerFilePicker}
                  className="border-muted-foreground/30 bg-background/60 mx-auto flex w-full max-w-3xl cursor-pointer flex-col items-center justify-center gap-4 rounded-2xl border-2 border-dashed px-8 py-12 text-center transition-colors hover:border-orange-400 hover:bg-orange-500/5"
                >
                  <div className="bg-muted flex size-16 items-center justify-center rounded-full">
                    <ImageIcon className="text-muted-foreground size-8" />
                  </div>
                  <div className="space-y-2">
                    <p className="text-xl font-semibold">Drop an image anywhere, or click to browse</p>
                    <p className="text-muted-foreground text-sm">
                      You can also paste from your clipboard with <Kbd>⌘V</Kbd>
                    </p>
                    <p className="text-muted-foreground text-sm">
                      Instant hex codes and contrast ratios. Your image never leaves your device.
                    </p>
                  </div>
                </button>
              )}

              {error && <p className="text-destructive text-sm">{error}</p>}
            </div>
          </section>

          {palettePairs.length > 0 && (
            <section className="bg-muted/20" ref={resultsRef}>
              <div className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-4 py-16">
                <div className="flex flex-col gap-3">
                  <h2 className="text-2xl font-semibold">Your color palette with hex codes</h2>
                  <p className="text-muted-foreground max-w-3xl">
                    The dominant colors extracted from your image, ranked by visual prominence. Copy any hex code with
                    one click, or select a color to preview an accessible text pairing.
                  </p>
                </div>

                {previewUrl && (
                  <Card className="bg-background border-none shadow-sm">
                    <CardContent className="flex flex-col gap-4 p-6">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <h3 className="text-lg font-medium">Color picker: get the hex code of any pixel</h3>
                        <p className="text-muted-foreground text-sm">Hover to preview, click to copy</p>
                      </div>
                      <div className="relative mx-auto w-full max-w-2xl">
                        <img
                          src={previewUrl}
                          alt="Pick a color from your image"
                          draggable={false}
                          onPointerMove={onPickerMove}
                          onPointerLeave={() => setHoverSample(null)}
                          onPointerDown={onPickerClick}
                          className="w-full cursor-crosshair rounded-lg border select-none"
                        />
                        {hoverSample && (
                          <div
                            className="bg-background pointer-events-none absolute z-10 flex items-center gap-1.5 rounded-md border px-2 py-1 font-mono text-xs shadow-md"
                            style={{ left: hoverSample.x + 14, top: hoverSample.y + 14 }}
                          >
                            <span className="size-3.5 rounded-sm border" style={{ backgroundColor: hoverSample.hex }} />
                            {hoverSample.hex}
                          </div>
                        )}
                      </div>
                      {pickedColors.length > 0 && (
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-muted-foreground text-xs uppercase">Picked</span>
                          {pickedColors.map((hex) => (
                            <button
                              key={hex}
                              type="button"
                              onClick={() => {
                                setSelectedColor(hex);
                                void copyHex(hex);
                              }}
                              className="border-border hover:bg-muted/60 flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-mono text-xs transition-colors"
                              title="Copy hex code"
                            >
                              <span className="size-3.5 rounded-full border" style={{ backgroundColor: hex }} />
                              {copiedHex === hex ? "Copied" : hex}
                            </button>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                <div className="flex h-16 w-full overflow-hidden rounded-xl border shadow-sm">
                  {palettePairs.map((entry) => (
                    <button
                      key={`strip-${entry.hex}`}
                      type="button"
                      onClick={() => setSelectedColor(entry.hex)}
                      title={`${entry.hex} (click to select)`}
                      className={cn(
                        "group relative flex-1 transition-[flex-grow] duration-200",
                        selectedColor === entry.hex && "flex-[1.6]",
                      )}
                      style={{ backgroundColor: entry.hex }}
                    >
                      <span
                        className={cn(
                          "absolute inset-x-0 bottom-1 mx-auto w-fit rounded px-1.5 py-0.5 font-mono text-[11px] opacity-0 transition-opacity group-hover:opacity-100",
                          selectedColor === entry.hex && "opacity-100",
                        )}
                        style={{ backgroundColor: entry.textColor, color: entry.hex }}
                      >
                        {entry.hex}
                      </span>
                    </button>
                  ))}
                </div>

                <div className="grid gap-6 md:grid-cols-2">
                  <Card className="bg-background border-none shadow-sm">
                    <CardContent className="flex flex-col gap-6 p-6">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-medium">Hex codes & contrast ratios</h3>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            void copyPalette();
                          }}
                        >
                          {copiedAll ? (
                            <>
                              <Check className="mr-2 size-4" /> Copied
                            </>
                          ) : (
                            <>
                              <Clipboard className="mr-2 size-4" /> Copy all codes
                            </>
                          )}
                        </Button>
                      </div>

                      <div className="grid gap-4">
                        {palettePairs.map((entry) => (
                          <div
                            key={entry.hex}
                            className={cn(
                              "flex items-center justify-between gap-3 rounded-xl border p-3 transition-all",
                              selectedColor === entry.hex
                                ? "border-orange-500 bg-orange-500/10"
                                : "border-border bg-background hover:bg-muted/60",
                            )}
                          >
                            <button
                              type="button"
                              onClick={() => setSelectedColor(entry.hex)}
                              className="flex min-w-0 flex-1 items-center gap-4 text-left"
                            >
                              <span
                                className="size-12 shrink-0 rounded-lg border"
                                style={{ backgroundColor: entry.hex }}
                              />
                              <div className="min-w-0">
                                <p className="font-mono text-base font-semibold">{entry.hex}</p>
                                <p className="text-muted-foreground text-xs">
                                  Contrast {entry.contrastRatio.toFixed(2)}:1 with {entry.textColor}
                                </p>
                              </div>
                            </button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => void copyHex(entry.hex)}
                              className="shrink-0"
                            >
                              {copiedHex === entry.hex ? (
                                <>
                                  <Check className="size-4 text-green-600" /> Copied
                                </>
                              ) : (
                                <>
                                  <Clipboard className="size-4" /> Copy
                                </>
                              )}
                            </Button>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {selectedPair && (
                    <Card className="bg-background border-none shadow-sm">
                      <CardContent className="flex flex-col gap-6 p-6">
                        <div className="flex items-center justify-between">
                          <h3 className="text-lg font-medium">Accessible color preview</h3>
                          <span className="text-muted-foreground text-sm">
                            Contrast {selectedPair.contrastRatio.toFixed(2)}:1
                          </span>
                        </div>

                        <div
                          className="flex flex-col gap-6 rounded-2xl p-8 shadow-inner"
                          style={{
                            backgroundColor: selectedPair.backgroundColor,
                            color: selectedPair.textColor,
                          }}
                        >
                          <p className="text-sm tracking-widest uppercase">Background {selectedPair.backgroundColor}</p>
                          <h4 className="text-3xl font-semibold">WCAG-compliant color combinations</h4>
                          <p className="max-w-md text-base opacity-80">
                            This pairing meets WCAG AA accessibility standards. Perfect for web design, social media
                            graphics, marketing banners, and branded content.
                          </p>
                          <div className="flex flex-wrap gap-3">
                            {palette
                              .filter((color) => color !== selectedPair.backgroundColor)
                              .map((color) => (
                                <span
                                  key={color}
                                  className="rounded-full border px-3 py-1 text-sm"
                                  style={{
                                    borderColor: selectedPair.textColor,
                                    color: selectedPair.textColor,
                                  }}
                                >
                                  {color}
                                </span>
                              ))}
                          </div>
                        </div>

                        <p className="text-muted-foreground text-sm">
                          Select any color above to preview it as a background with the best-contrast text color from
                          your palette.
                        </p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            </section>
          )}

          <section className={palettePairs.length > 0 ? "" : "bg-muted/20"}>
            <div className="mx-auto flex w-full max-w-5xl flex-col gap-10 px-4 py-16">
              <div className="text-center">
                <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">
                  How the color palette extractor works
                </h2>
                <p className="text-muted-foreground mx-auto mt-4 max-w-2xl text-lg">
                  From photo to accessible color palette in four quick steps. No uploads, no servers, just instant
                  results in your browser.
                </p>
              </div>

              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                {HOW_IT_WORKS_STEPS.map((step, index) => {
                  const Icon = step.icon;
                  return (
                    <Card key={step.title} className="bg-background border-none shadow-sm">
                      <CardContent className="flex flex-col gap-4 p-6">
                        <div className="flex size-12 shrink-0 items-center justify-center rounded-xl bg-orange-500/10 text-orange-600 dark:text-orange-400">
                          <Icon className="size-6" />
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <span className="flex size-6 items-center justify-center rounded-full bg-orange-500/20 text-xs font-semibold text-orange-600 dark:text-orange-400">
                              {index + 1}
                            </span>
                            <h3 className="text-lg font-semibold">{step.title}</h3>
                          </div>
                          <p className="text-muted-foreground text-sm">{step.description}</p>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </section>
        </main>

        <FinalCta />
      </div>
      <SiteFooter />
    </>
  );
}

function extractPaletteSamples(image: HTMLImageElement) {
  const maxDimension = 420;
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) {
    throw new Error("Canvas is not supported in this browser");
  }

  const scale = Math.min(1, maxDimension / Math.max(image.width, image.height));
  const width = Math.max(1, Math.round(image.width * scale));
  const height = Math.max(1, Math.round(image.height * scale));

  canvas.width = width;
  canvas.height = height;
  context.drawImage(image, 0, 0, width, height);

  const { data } = context.getImageData(0, 0, width, height);
  const totalPixels = width * height;
  const targetSamples = 8000;
  const stride = Math.max(1, Math.floor(totalPixels / targetSamples));

  const samples: string[] = [];
  for (let i = 0; i < totalPixels; i += stride) {
    const offset = i * 4;
    const alpha = data[offset + 3];
    if (alpha < 64) continue;
    const r = quantizeChannel(data[offset]);
    const g = quantizeChannel(data[offset + 1]);
    const b = quantizeChannel(data[offset + 2]);
    samples.push(rgbToHex(r, g, b));
  }

  return samples;
}

function quantizeChannel(value: number): number {
  const step = 17;
  const quantized = Math.round(value / step) * step;
  return Math.max(0, Math.min(255, quantized));
}

function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (value: number) => value.toString(16).padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase();
}
